import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { collectionsService } from "../../services/collections";
import api from "../../services/api";
import { getImageUrl } from "../../utils/imageUtils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
    GripVertical,
    Upload,
    Save,
    Loader2,
    ImageOff,
    Star,
    Layers,
    X,
    AlertCircle,
    CheckCircle2,
    RotateCcw,
    Search,
    Eye,
    EyeOff
} from "lucide-react";
import { cn } from "@/lib/utils";

export default function AdminFeaturedCollections() {
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");

    // Drag state
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const dragCounter = useRef({});

    useEffect(() => {
        fetchCollections();
    }, []);

    const fetchCollections = async () => {
        try {
            setLoading(true);
            const data = await collectionsService.getAll({ limit: 1000, skip_populate: true });
            const items = data.data || data || [];

            // Sort: featured first (by featured_order), then non-featured alphabetically
            const sorted = [...items].sort((a, b) => {
                if (a.is_featured && !b.is_featured) return -1;
                if (!a.is_featured && b.is_featured) return 1;
                if (a.is_featured && b.is_featured) {
                    const orderA = a.featured_order || 999;
                    const orderB = b.featured_order || 999;
                    return orderA - orderB;
                }
                return a.name.localeCompare(b.name);
            });

            setCollections(sorted);
            setHasChanges(false);
        } catch (error) {
            toast.error("Failed to fetch collections");
        } finally {
            setLoading(false);
        }
    };

    // Toggle featured status
    const handleToggleFeatured = async (collection) => {
        const colId = collection.collection_id || collection._id;
        const isFeatured = !collection.is_featured;
        try {
            await collectionsService.update(colId, { is_featured: isFeatured });

            setCollections(prev => {
                const updated = prev.map(c =>
                    (c.collection_id || c._id) === colId
                        ? { ...c, is_featured: isFeatured }
                        : c
                );
                // Re-sort: featured first, then non-featured
                return updated.sort((a, b) => {
                    if (a.is_featured && !b.is_featured) return -1;
                    if (!a.is_featured && b.is_featured) return 1;
                    return 0; // keep current relative order within groups
                });
            });

            setHasChanges(true);
            toast.success(isFeatured ? `"${collection.name}" added to featured` : `"${collection.name}" removed from featured`);
        } catch (error) {
            toast.error("Failed to update collection");
        }
    };

    // --- Drag & Drop Handlers ---
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = "move";

        // Create a styled clone for the drag image so user sees what they're dragging
        const el = e.currentTarget;
        const clone = el.cloneNode(true);
        clone.style.position = 'absolute';
        clone.style.top = '-9999px';
        clone.style.left = '-9999px';
        clone.style.width = Math.min(el.offsetWidth, 800) + 'px';
        clone.style.maxWidth = '800px';
        clone.style.overflow = 'hidden';
        clone.style.opacity = '0.85';
        clone.style.transform = 'rotate(1deg) scale(1.02)';
        clone.style.boxShadow = '0 12px 40px rgba(0,0,0,0.18)';
        clone.style.borderRadius = '12px';
        clone.style.border = '2px solid #6366f1';
        clone.style.background = 'white';
        clone.style.zIndex = '9999';
        document.body.appendChild(clone);
        e.dataTransfer.setDragImage(clone, Math.min(el.offsetWidth, 800) / 2, el.offsetHeight / 2);
        // Clean up the clone after the browser grabs the snapshot
        requestAnimationFrame(() => {
            document.body.removeChild(clone);
        });
    };

    const handleDragEnter = (e, index) => {
        e.preventDefault();
        dragCounter.current[index] = (dragCounter.current[index] || 0) + 1;
        if (index !== draggedIndex) {
            setDragOverIndex(index);
        }
    };

    const handleDragLeave = (e, index) => {
        dragCounter.current[index] = (dragCounter.current[index] || 0) - 1;
        if (dragCounter.current[index] <= 0) {
            dragCounter.current[index] = 0;
            if (dragOverIndex === index) {
                setDragOverIndex(null);
            }
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        dragCounter.current = {};

        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const updated = [...collections];
        const [dragged] = updated.splice(draggedIndex, 1);
        updated.splice(dropIndex, 0, dragged);

        setCollections(updated);
        setDraggedIndex(null);
        setDragOverIndex(null);
        setHasChanges(true);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
        dragCounter.current = {};
    };

    // --- Save Order ---
    const handleSaveOrder = async () => {
        try {
            setSaving(true);
            // Only save order for featured collections
            const featuredOrder = collections
                .filter(c => c.is_featured)
                .map(c => c.collection_id);
            if (featuredOrder.length > 0) {
                await collectionsService.reorder(featuredOrder);
            }
            toast.success("Featured collection order saved!");
            setHasChanges(false);
        } catch (error) {
            toast.error("Failed to save order");
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveImage = async (collectionId) => {
        try {
            await collectionsService.updateImage(collectionId, "");
            setCollections(prev =>
                prev.map(c =>
                    c.collection_id === collectionId ? { ...c, cover_image: "" } : c
                )
            );
            toast.success("Thumbnail removed");
        } catch (err) {
            toast.error("Failed to remove image");
        }
    };

    // Filter by applied search (only triggered on Enter or button click)
    const filteredCollections = appliedSearch
        ? collections.filter(c => c.name.toLowerCase().includes(appliedSearch.toLowerCase()))
        : collections;

    const featuredCount = collections.filter(c => c.is_featured).length;

    // --- Loading State ---
    if (loading) {
        return (
            <div className="space-y-6 animate-fade-in">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="h-8 w-64 bg-slate-200 rounded animate-pulse" />
                        <div className="h-4 w-96 bg-slate-100 rounded animate-pulse mt-2" />
                    </div>
                </div>
                <div className="space-y-3">
                    {[1, 2, 3, 4, 5, 6].map(i => (
                        <div key={i} className="h-20 bg-white rounded-xl border border-slate-100 animate-pulse" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                        <Star className="w-6 h-6 text-amber-500" />
                        Featured Collections
                    </h2>
                    <p className="text-slate-500 mt-1">
                        Toggle collections to feature them, then drag and drop to re-arrange. Upload thumbnails to fix broken images.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {hasChanges && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="text-slate-600 border-slate-200"
                            onClick={fetchCollections}
                        >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Reset
                        </Button>
                    )}
                    <Button
                        onClick={handleSaveOrder}
                        disabled={!hasChanges || saving}
                        className={cn(
                            "bg-indigo-600 hover:bg-indigo-700 shadow-lg transition-all",
                            hasChanges ? "shadow-indigo-200 scale-100" : "shadow-none opacity-60"
                        )}
                    >
                        {saving ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Save className="w-4 h-4 mr-2" />
                        )}
                        Save Order
                    </Button>
                </div>
            </div>

            {/* Unsaved changes banner */}
            {hasChanges && (
                <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>You have unsaved changes. Click <strong>Save Order</strong> to apply the new arrangement.</span>
                </div>
            )}

            {/* Search + stats bar */}
            <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Badge variant="secondary" className="bg-amber-50 text-amber-700 border-amber-200 text-xs px-2.5 py-1">
                        <Star className="w-3 h-3 mr-1 fill-amber-500 text-amber-500" />
                        {featuredCount} Featured
                    </Badge>
                    <span className="text-xs text-slate-400">
                        {collections.length} total collections
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative w-64">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                        <Input
                            placeholder="Search collections..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    setAppliedSearch(searchTerm);
                                }
                            }}
                            className="pl-9 h-9"
                        />
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-9 px-3"
                        onClick={() => setAppliedSearch(searchTerm)}
                    >
                        <Search className="w-4 h-4" />
                    </Button>
                    {appliedSearch && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-9 px-2 text-slate-500 hover:text-slate-700"
                            onClick={() => {
                                setSearchTerm("");
                                setAppliedSearch("");
                            }}
                        >
                            <X className="w-4 h-4 mr-1" />
                            Clear
                        </Button>
                    )}
                </div>
            </div>

            {/* Unified Collection List */}
            {filteredCollections.length === 0 ? (
                <div className="card-elevated p-12 text-center">
                    <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Layers className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                        {searchTerm ? "No Results" : "No Collections"}
                    </h3>
                    <p className="text-slate-500 max-w-md mx-auto">
                        {searchTerm
                            ? "No collections match your search."
                            : "Create collections from the Collections page first."}
                    </p>
                </div>
            ) : (
                <div className="space-y-1.5">
                    {filteredCollections.map((col, index) => {
                        const isDragged = draggedIndex === index;
                        const isDragOver = dragOverIndex === index;
                        const hasBrokenImage = col.is_featured && !col.cover_image;
                        // Detect the boundary between featured and non-featured
                        const prevCol = index > 0 ? filteredCollections[index - 1] : null;
                        const isBoundary = prevCol && prevCol.is_featured && !col.is_featured;

                        return (
                            <div key={col.collection_id || col._id}>
                                {/* Divider between featured and non-featured */}
                                {isBoundary && (
                                    <div className="flex items-center gap-3 py-3 mt-2">
                                        <div className="flex-1 h-px bg-slate-200" />
                                        <span className="text-xs text-slate-400 font-medium uppercase tracking-wider flex items-center gap-1.5">
                                            <Layers className="w-3.5 h-3.5" />
                                            Other Collections
                                        </span>
                                        <div className="flex-1 h-px bg-slate-200" />
                                    </div>
                                )}

                                <div
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragEnter={(e) => handleDragEnter(e, index)}
                                    onDragLeave={(e) => handleDragLeave(e, index)}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleDrop(e, index)}
                                    onDragEnd={handleDragEnd}
                                    className={cn(
                                        "group flex items-center gap-4 p-3.5 rounded-xl border transition-all duration-200 cursor-grab active:cursor-grabbing relative",
                                        isDragged && "opacity-30 scale-[0.97] border-2 border-dashed border-indigo-300 bg-indigo-50/40 shadow-none",
                                        isDragOver && !isDragged && "border-indigo-400 bg-indigo-50/30 shadow-sm ring-2 ring-indigo-200 ring-offset-1",
                                        !isDragged && !isDragOver && col.is_featured
                                            ? "border-amber-100 bg-amber-50/20 hover:border-amber-200 hover:shadow-sm"
                                            : !isDragged && !isDragOver
                                                ? "border-slate-100 bg-white hover:border-slate-200 hover:shadow-sm"
                                                : "",
                                        hasBrokenImage && "border-red-200 bg-red-50/30"
                                    )}
                                >
                                    {/* Drop indicator line */}
                                    {isDragOver && !isDragged && (
                                        <div className="absolute -top-[5px] left-4 right-4 flex items-center z-30 pointer-events-none">
                                            <div className="w-2.5 h-2.5 rounded-full bg-indigo-500 border-2 border-white shadow-sm flex-shrink-0" />
                                            <div className="flex-1 h-[2px] bg-indigo-500 rounded-full" />
                                            <div className="w-2.5 h-2.5 rounded-full bg-indigo-500 border-2 border-white shadow-sm flex-shrink-0" />
                                        </div>
                                    )}
                                    {/* Drag Handle + Position */}
                                    <div className="flex-shrink-0 flex items-center gap-2">
                                        <GripVertical className="w-5 h-5 text-slate-300 group-hover:text-slate-500 transition-colors" />
                                        {col.is_featured && (
                                            <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-[11px] font-bold flex items-center justify-center">
                                                {collections.filter(c => c.is_featured).indexOf(col) + 1}
                                            </span>
                                        )}
                                    </div>

                                    {/* Thumbnail */}
                                    <div className="flex-shrink-0 relative w-14 h-14 rounded-lg overflow-hidden border border-slate-200 bg-slate-50 group/thumb" draggable="false" onMouseDown={(e) => e.stopPropagation()}>
                                        {col.cover_image ? (
                                            <>
                                                <img
                                                    src={getImageUrl(col.cover_image)}
                                                    alt={col.name}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                                <div className="absolute inset-0 items-center justify-center bg-slate-100 text-slate-400 hidden">
                                                    <ImageOff className="w-5 h-5" />
                                                </div>
                                                <button
                                                    type="button"
                                                    draggable="false"
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        handleRemoveImage(col.collection_id);
                                                    }}
                                                    className="absolute top-0.5 right-0.5 p-0.5 bg-white/90 rounded-full text-slate-500 hover:text-red-600 shadow-sm opacity-0 group-hover/thumb:opacity-100 transition-opacity z-10"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </>
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-slate-300">
                                                <ImageOff className="w-5 h-5" />
                                            </div>
                                        )}

                                        {/* Upload overlay */}
                                        <label
                                            className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover/thumb:opacity-100 cursor-pointer transition-opacity z-20"
                                            draggable="false"
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <Upload className="w-4 h-4 text-white" />
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={async (e) => {
                                                    e.stopPropagation();
                                                    const file = e.target.files[0];
                                                    if (!file) return;
                                                    const fd = new FormData();
                                                    fd.append('image', file);
                                                    try {
                                                        const res = await api.post('/upload/collection-image', fd, {
                                                            headers: { 'Content-Type': 'multipart/form-data' }
                                                        });
                                                        const newPath = res.data.path;
                                                        await collectionsService.updateImage(col.collection_id, newPath);
                                                        setCollections(prev =>
                                                            prev.map(c =>
                                                                c.collection_id === col.collection_id ? { ...c, cover_image: newPath } : c
                                                            )
                                                        );
                                                        toast.success('Thumbnail updated!');
                                                    } catch (err) {
                                                        toast.error('Upload failed');
                                                    }
                                                    e.target.value = '';
                                                }}
                                            />
                                        </label>
                                    </div>

                                    {/* Collection Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h3 className={cn(
                                                "font-semibold truncate",
                                                col.is_featured ? "text-slate-900" : "text-slate-600"
                                            )}>
                                                {col.name}
                                            </h3>
                                            {hasBrokenImage && (
                                                <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-5 bg-red-100 text-red-700 border-red-200 hover:bg-red-100 flex-shrink-0">
                                                    <ImageOff className="w-3 h-3 mr-1" />
                                                    No Image
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3 mt-1">
                                            <span className="text-xs text-slate-400">
                                                {col.product_count || 0} products
                                            </span>
                                            <span className={cn(
                                                "text-[10px] font-medium px-1.5 py-0.5 rounded-full",
                                                col.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"
                                            )}>
                                                {col.is_active ? "Active" : "Inactive"}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Upload Button + Featured Toggle */}
                                    <div className="flex-shrink-0 flex items-center gap-3" draggable="false" onMouseDown={(e) => e.stopPropagation()}>
                                        <label className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity" draggable="false" onMouseDown={(e) => e.stopPropagation()}>
                                            <div className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white px-3 h-8 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors cursor-pointer">
                                                <Upload className="w-3.5 h-3.5 mr-1.5" />
                                                Upload
                                            </div>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={async (e) => {
                                                    e.stopPropagation();
                                                    const file = e.target.files[0];
                                                    if (!file) return;
                                                    const fd = new FormData();
                                                    fd.append('image', file);
                                                    try {
                                                        const res = await api.post('/upload/collection-image', fd, {
                                                            headers: { 'Content-Type': 'multipart/form-data' }
                                                        });
                                                        const newPath = res.data.path;
                                                        await collectionsService.updateImage(col.collection_id, newPath);
                                                        setCollections(prev =>
                                                            prev.map(c =>
                                                                c.collection_id === col.collection_id ? { ...c, cover_image: newPath } : c
                                                            )
                                                        );
                                                        toast.success('Thumbnail updated!');
                                                    } catch (err) {
                                                        toast.error('Upload failed');
                                                    }
                                                    e.target.value = '';
                                                }}
                                            />
                                        </label>
                                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                            <Switch
                                                checked={col.is_featured}
                                                onCheckedChange={() => handleToggleFeatured(col)}
                                                className="data-[state=checked]:bg-amber-500"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Sticky save bar when there are changes */}
            {hasChanges && (
                <div className="sticky bottom-4 z-10">
                    <div className="flex items-center justify-between bg-slate-900 text-white rounded-xl px-6 py-4 shadow-2xl">
                        <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                            <span className="text-sm font-medium">{featuredCount} featured collections</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-slate-300 hover:text-white hover:bg-white/10"
                                onClick={fetchCollections}
                            >
                                Discard
                            </Button>
                            <Button
                                size="sm"
                                className="bg-indigo-500 hover:bg-indigo-400 text-white"
                                onClick={handleSaveOrder}
                                disabled={saving}
                            >
                                {saving ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4 mr-2" />
                                )}
                                Save Order
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
